// src/marketing/channels/hooks/useMarketingActions.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import type { MarketingAction } from '../../../lib/types'

const ACTIONS_STALE = 1000 * 60 * 2

const actionKeys = {
  all: ['marketing-actions'] as const,
  list: (appId: string, channel?: string) =>
    channel
      ? (['marketing-actions', 'list', appId, channel] as const)
      : (['marketing-actions', 'list', appId] as const),
}

export function useMarketingActions(appId: string | null, channel?: string) {
  return useQuery<MarketingAction[]>({
    queryKey: appId ? actionKeys.list(appId, channel) : actionKeys.all,
    queryFn: () => {
      const qs = channel ? `&channel=${channel}` : ''
      return api
        .get<{ actions: MarketingAction[] }>(`/api/marketing-actions?appId=${appId}${qs}`)
        .then((r) => r.actions)
    },
    enabled: appId !== null,
    staleTime: ACTIONS_STALE,
  })
}

export function useCreateMarketingAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<MarketingAction> & { appId: string; channel: MarketingAction['channel']; title: string }) =>
      api.post<{ action: MarketingAction }>('/api/marketing-actions', body).then((r) => r.action),
    onSuccess: () => void qc.invalidateQueries({ queryKey: actionKeys.all }),
  })
}

export function useUpdateMarketingAction(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<MarketingAction>) =>
      api.put<{ action: MarketingAction }>(`/api/marketing-actions/${id}`, body).then((r) => r.action),
    onSuccess: () => void qc.invalidateQueries({ queryKey: actionKeys.all }),
  })
}

export function useDeleteMarketingAction() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id: string) =>
      api.del<{ success: boolean }>(`/api/marketing-actions/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: actionKeys.all }),
  })
}
